import React from 'react';
import clsx from 'clsx';
import {i18n} from '@netx/core/i18n';
import {TextField} from '@netx/core-components/TextField';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import type {CommonRuleEditorProps, RuleValidationErrors} from '../../../types';
import {
	advancedRawRuleEditorClasses,
	getAdvancedRawRuleEditorUtilityClass,
	AdvancedRawRuleEditorClasses,
	ADVANCED_RAW_RULE_EDITOR_CLASS_NAME,
} from './advancedRawRuleEditorClasses';

export type AdvancedRawRuleEditorOwnerState = WithClasses<
	AdvancedRawRuleEditorClasses,
	{}
>;

const useUtilityClasses = (ownerState: AdvancedRawRuleEditorOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getAdvancedRawRuleEditorUtilityClass, classes);
};

const AdvancedRawRuleEditorRoot = styled('div', {
	name: ADVANCED_RAW_RULE_EDITOR_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
}));

export type AdvancedRawRuleEditorProps = WithClasses<
	AdvancedRawRuleEditorClasses,
	Pick<CommonRuleEditorProps, 'validationErrors'> & {
		elementValue1: string;
		onValue1Change: (value: string) => void;
	}
>;

const AdvancedRawRuleEditorInner = ({
	classes: _classes,
	className,
	elementValue1,
	validationErrors,
	onValue1Change,
}: AdvancedRawRuleEditorProps) => {
	const ownerState: AdvancedRawRuleEditorOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<AdvancedRawRuleEditorRoot className={clsx(classes.root, className)}>
			<TextField
				data-testid="advanced-raw-rule-value-1"
				fullWidth
				name="rule-value-1"
				label={i18n.t('i18n.value')}
				required
				value={elementValue1.toString()}
				onChange={(event, value) => onValue1Change(value)}
				error={validationErrors?.elementValue1?.message}
			/>
		</AdvancedRawRuleEditorRoot>
	);
};

export const AdvancedRawRuleEditorPrimitive = AdvancedRawRuleEditorInner;

export const AdvancedRawRuleEditor = AdvancedRawRuleEditorPrimitive;

export default AdvancedRawRuleEditor;

export type AdvancedRawRuleEditorGenericProps = WithClasses<
	AdvancedRawRuleEditorClasses,
	CommonRuleEditorProps & {}
>;

/**
 * Generic wrapper to accomodate shared rule pattern differences
 */
export const AdvancedRawRuleEditorGenericPrimitive = ({
	elementValue1,
	...props
}: AdvancedRawRuleEditorGenericProps) => {
	return (
		<AdvancedRawRuleEditor
			{...props}
			elementValue1={elementValue1.toString()}
		/>
	);
};

export const AdvancedRawRuleEditorGeneric =
	AdvancedRawRuleEditorGenericPrimitive;
